import type { ApplicationDefinitionConditionalEditStore } from '../stores';
import { LayoutNodeDto, LayoutStructureDto } from '../types';
import { defaultChildBindingGroup } from './defaultChildBindingGroup';
import { BindingGroupBaseline } from './serializeBindingGroupContent';

type BaselineCache = {
    meta: string | undefined;
    sections: string;
    byLayout: Map<string | null, BindingGroupBaseline[]>;
};

const caches = new WeakMap<ApplicationDefinitionConditionalEditStore, BaselineCache>();

const defaultNodes = (nodes: LayoutNodeDto[]): BindingGroupBaseline[] =>
    nodes.reduce<BindingGroupBaseline[]>((groups, node) => {
        for (let i = 1; i <= node.count; i++) {
            groups.push({
                ...defaultChildBindingGroup(node, i),
                enabled: true,
                childBindingGroups: defaultNodes(node.childNodes)
            });
        }
        return groups;
    }, []);

export const getDefaultBindingGroups = (
    store: ApplicationDefinitionConditionalEditStore,
    layoutSettingsId: string | null
): BindingGroupBaseline[] => {
    const meta = store.applicationDefinition?.meta;
    const sections = store.applicationDefinitionSectionNames;
    const sectionKey = JSON.stringify(sections);
    let cache = caches.get(store);
    if (!cache || cache.meta !== meta || cache.sections !== sectionKey) {
        cache = { meta, sections: sectionKey, byLayout: new Map() };
        caches.set(store, cache);
    }
    const cached = cache.byLayout.get(layoutSettingsId);
    if (cached) return cached;

    const metadata: { layoutStructure?: LayoutStructureDto } = meta ? JSON.parse(meta) : {};
    const layout = metadata.layoutStructure?.layoutSettings.find(candidate => candidate.id === layoutSettingsId);
    const baseline = sections
        .filter(section => !layout?.hiddenSections.includes(section))
        .map(section => ({
            bindingGroupId: section.replace(/\s+/g, ''),
            name: section,
            enabled: true,
            childBindingGroups:
                layout?.layoutNodes.length && !layout.sectionsWithDefaultLayout.includes(section)
                    ? defaultNodes(layout.layoutNodes)
                    : []
        }));
    cache.byLayout.set(layoutSettingsId, baseline);
    return baseline;
};
